import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  DialogTitle,
  Dialog,
  IconButton,
} from '@mui/material';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { ArrowBack, NavigateBefore, NavigateNext, Download } from '@mui/icons-material';
import { Loading } from './Loading';
import { useTranslation } from 'react-i18next';
import { captureException } from '../utils/captureException';

// Configure PDF.js worker
pdfjs.GlobalWorkerOptions.workerSrc = '/pdf.worker.min.mjs';

interface PdfViewerProps {
  open: boolean;
  onClose: () => void;
  pdfPath?: string;
  title?: string;
}

const PdfViewer: React.FC<PdfViewerProps> = ({ 
  open, 
  onClose, 
  pdfPath, 
  title = "Lanzamiento" 
}) => {
  const { t } = useTranslation();
  const [numPages, setNumPages] = useState<number | null>(null);
  const [pageNumber, setPageNumber] = useState<number>(1);
  const [pdfLoading, setPdfLoading] = useState(true);

  useEffect(() => {
    if (open && !pdfPath) {
      setPdfLoading(false);
    }
  }, [open, pdfPath]);

  
  const onDocumentLoadSuccess = ({ numPages }: { numPages: number }) => {
    setNumPages(numPages);
    setPdfLoading(false);
  };
  
  const onDocumentLoadError = (error: Error) => {
    captureException(error, { tags: { feature: 'pdfViewer' }, extra: { action: 'loadDocument', pdfPath } });
    console.error('Error loading PDF:', error);
    setPdfLoading(false);
  };

  const goToPrevPage = () => {
    setPageNumber(prevPageNumber => Math.max(prevPageNumber - 1, 1));
  };

  const goToNextPage = () => {
    setPageNumber(prevPageNumber => Math.min(prevPageNumber + 1, numPages || 1));
  };

  const handleClose = () => {
    setPageNumber(1);
    setNumPages(null);
    onClose();
  };

  const handleDownload = async () => {
    if (!pdfPath) return;
    
    try {
      // Use the signed URL directly from GCS
      const response = await fetch(pdfPath);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${title || 'document'}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      captureException(error, { tags: { feature: 'pdfViewer' }, extra: { action: 'downloadPdf', pdfPath } });
      console.error('Error downloading PDF:', error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth
      fullScreen
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 100,
      }}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {/* First row: Title, Download and Close buttons */}
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">{title}</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {pdfPath && (
                <IconButton 
                  onClick={handleDownload} 
                  title={t('pdfViewer.download')}
                  color="primary"
                >
                  <Download />
                </IconButton>
              )}
              <IconButton onClick={handleClose} title={t('pdfViewer.close')}>
                <ArrowBack />
              </IconButton>
            </Box>
          </Box>
          
          {/* Second row: PDF controls */}
          {pdfPath && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'center' }}>
            {numPages && (
              <Typography variant="body2" sx={{ mr: 2 }}>
                {t('pdfViewer.pageOf', { current: pageNumber, total: numPages })}
              </Typography>
            )}
            <IconButton 
              onClick={goToPrevPage} 
              disabled={pageNumber <= 1}
              size="small"
              title={t('pdfViewer.previousPage')}
            >
              <NavigateBefore />
            </IconButton>
            <IconButton 
              onClick={goToNextPage} 
              disabled={pageNumber >= (numPages || 1)}
              size="small"
              title={t('pdfViewer.nextPage')}
            >
              <NavigateNext />
            </IconButton>
          </Box>
          )}
        </Box>
      </DialogTitle>
      <Box sx={{ p: 0, height: '100%', display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
        {pdfLoading && <Loading />}
        {pdfPath && (
          <Document
            file={pdfPath}
            onLoadSuccess={onDocumentLoadSuccess}
            onLoadError={onDocumentLoadError}
            loading="Cargando PDF..."
          >
            <Box sx={{ maxWidth: '100vw', maxHeight: 'calc(100vh - 114px)', overflow: 'auto' }}>
              <Page 
                pageNumber={pageNumber} 
                  width={Math.min(window.innerWidth, 1200)}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
            </Box>
          </Document>
        )}
        {!pdfPath && !pdfLoading && (
          <Typography variant="body2">No se encontró el PDF de este Lanzamiento</Typography>
        )}
      </Box>
    </Dialog>
  );
};

export default PdfViewer;
